import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Lightbulb, RefreshCw, Loader2, ArrowRight, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OptimizationInsightsPanel = ({ qrId }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchCached();
  }, [qrId]);

  const fetchCached = async () => {
    try {
      const res = await axios.get(`${API}/intelligence/optimization-insights/${qrId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.status !== 'not_analyzed') {
        setData(res.data);
      }
    } catch {
      // No cached data
    } finally {
      setInitialLoad(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/intelligence/optimization-insights/${qrId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      setData(res.data);
      toast.success('Optimization insights generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate insights');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) return null;

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="optimization-insights-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-amber-500" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Optimization Insights
          </h2>
          <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
            AI Interview Engine
          </span>
        </div>
        <Button
          size="sm"
          onClick={generate}
          disabled={loading}
          className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
          data-testid="generate-optimization-insights-btn"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Analyzing...</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-1.5" /> {data ? 'Refresh' : 'Generate Insights'}</>
          )}
        </Button>
      </div>

      {!data && !loading && (
        <p className="text-sm text-[#475569] text-center py-6">
          Generate AI-powered optimization insights by combining question quality analysis with interview efficiency data.
        </p>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          <p className="text-sm text-[#475569]">AI is analyzing your interviews for optimization opportunities...</p>
        </div>
      )}

      {data && !loading && data.insights?.length > 0 && (
        <div className="space-y-3" data-testid="optimization-insights-list">
          {data.insights.map((ins, i) => (
            <div
              key={`item-${i}`}
              className="p-4 rounded-lg border border-[#E2E8F0] bg-[#FFFBF5] hover:border-amber-300 transition-colors"
              data-testid={`optimization-insight-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className="p-1.5 rounded-lg bg-amber-100 shrink-0 mt-0.5">
                  <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] mb-1">{ins.insight}</p>
                  <div className="flex items-start gap-1.5 mb-2">
                    <BookOpen className="h-3 w-3 text-[#475569] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#475569]">{ins.evidence}</p>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <ArrowRight className="h-3 w-3 text-[#1d4ed8] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#1d4ed8] font-medium">{ins.recommended_action}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {data.generated_at && (
            <p className="text-[10px] text-[#94A3B8] text-right pt-1">
              Last generated: {new Date(data.generated_at).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {data && !loading && (!data.insights || data.insights.length === 0) && (
        <p className="text-sm text-green-700 bg-green-50 p-3 rounded-lg border border-green-200 text-center">
          No optimization issues detected. Your interview configuration looks solid.
        </p>
      )}
    </Card>
  );
};
